import * as React from 'react';
// @ts-expect-error
import {LocationShape} from 'react-router-dom';

import classnames from 'classnames';

import type {ButtonVariant} from '../button/types';
import {ButtonIcon} from '../icon/button-icon';

import styles from './link.module.css';
import type {LinkVariant} from './types';

export type Size = 'mini' | 'tiny' | 'small' | 'normal' | 'big' | 'xlarge';

type Props = {
    as?: string | React.ComponentType<any>;
    newTab?: boolean;
    children: string | React.ReactNode;
    variant?: LinkVariant;
    size?: Size;
    icon?: React.ComponentType<any>;
    href?: string | null | undefined;
    to?: string | LocationShape;
    onClick?: (event?: any) => any;
    preventUnderline?: boolean;
    isFullWidth?: boolean;
    isBold?: boolean;
    shouldAddHttpProtocol?: boolean;
    shouldTruncate?: boolean;
    widthFitContent?: boolean;
    className?: string;
};

/**
 * Use this to create an anchor tag, or a react-router link by providing an `as` prop.
 * Variants can be used to control the style of the link, including making
 * it appear as a button if desired.
 */
export function Link(props: Props) {
    const {
        as = 'a',
        icon,
        variant = 'secondary',
        size = 'normal',
        children,
        newTab,
        preventUnderline,
        isFullWidth,
        isBold,
        shouldAddHttpProtocol,
        href,
        shouldTruncate,
        className,
        ...restProps
    } = props;
    const styleName = classnames(
        styles[variant],
        styles[size],
        {
            [styles['prevent-underline']]: preventUnderline,
        },
        {
            [styles['full-width']]: isFullWidth,
        },
        {
            [styles.bold]: isBold,
        },
        {
            [styles.truncate]: shouldTruncate,
        },
        {
            [styles['width-fit-content']]: props.widthFitContent,
        }
    );

    const Tag = as;
    const hrefPattern = /^https?:/;
    const fullHref =
        href && shouldAddHttpProtocol && !hrefPattern.test(href) ? `https://${href}` : href;

    return (
        <Tag
            target={newTab ? '_blank' : undefined}
            rel={newTab ? 'noreferrer noopener' : undefined}
            href={fullHref}
            {...restProps}
            className={classnames(styleName, className)}
        >
            {icon ? (
                <ButtonIcon size={size} variant={getButtonVariant(variant)} icon={icon} />
            ) : undefined}
            {children}
        </Tag>
    );
}

function getButtonVariant(variant: LinkVariant): ButtonVariant {
    switch (variant) {
        case 'button':
        case 'button-primary':
            return 'primary';
        case 'button-ai-blue':
            return 'ai-blue';
        case 'button-save':
            return 'save';
        case 'primary':
            return 'text-primary';
        case 'danger':
            return 'text-danger';
        case 'button-upgrade':
            return 'upgrade';
        case 'ai':
            return 'text-ai';
        case 'button-ai-purple-dark':
            return 'ai-purple-dark';
        default:
            return 'secondary';
    }
}
